import React, { Fragment, useMemo } from 'react';
import classNames from 'classnames';

import { Checkbox, Tooltip } from '@hcs/design-system';
import { Table, TableCell, TableRow } from '@hcs/design-system';
import { LoadingSpinner } from '@hcs/design-system';
import { useDexpEndpointTiers } from '@hcs/hc-products';
import { useOrgSelfServeInfo } from '@hcs/self-serve-info';
import { SELF_SERVICE_TIERS_MAPPING } from '@hcs/self-service';
import {
  DexpEndpoint,
  DexpEndpointLevel,
  DexpEndpointsParams,
} from '@hcs/types';
import { formatMoney } from '@hcs/utils';

import { useDexpEndpointsTable } from '../../hooks/useDexpEndpointsTable';
import {
  dexpFixEndpointId,
  getEndpointTierLabelAndCostKey,
} from '../../utils/endpointId.utils';

import styles from './EndpointsDataTable.module.css';

interface Props {
  endpointLevel: DexpEndpointLevel | 'all';
  filters?: DexpEndpointsParams;
  selectable?: boolean;
}

const dataHcName = 'endpoints-data-table';

export const EndpointsDataTable = ({
  endpointLevel,
  selectable = true,
  filters: filtersProp,
}: Props) => {
  const {
    state: { filters, dexpEndpointsQuery, endpointsInShoppingCart },
    actions: { addToCart, removeFromCart },
  } = useDexpEndpointsTable(filtersProp);
  const { data: dexpTiers } = useDexpEndpointTiers();
  const { data: orgSsInfo } = useOrgSelfServeInfo();
  const { data: endpoints, isInitialLoading } = dexpEndpointsQuery;
  const { availableEndpoints, unavailableEndpoints } = useMemo(() => {
    const filteredEndpoints: DexpEndpoint[] = [];
    const available: DexpEndpoint[] = [];
    const unavailable: DexpEndpoint[] = [];
    endpoints?.forEach((endpoint) => {
      if (endpointLevel === 'all' || endpoint.level === endpointLevel) {
        filteredEndpoints.push(endpoint);
        if (endpoint.access) {
          available.push(endpoint);
        } else {
          unavailable.push(endpoint);
        }
      }
    });
    return {
      filteredEndpoints,
      availableEndpoints: available,
      unavailableEndpoints: unavailable,
    };
  }, [endpoints, endpointLevel]);

  const getTableRows = (filteredEndpoints: DexpEndpoint[]) => {
    const currentSsTier =
      orgSsInfo?.self_serve_plan?.name ||
      // If orginfo has active_plan but no self_service_plan then have cancelled their subscription but their trial has not expired
      (orgSsInfo?.active_plan ? 'Pro' : undefined);
    const tierConfig =
      currentSsTier && SELF_SERVICE_TIERS_MAPPING[currentSsTier];
    return filteredEndpoints?.map((endpoint, i) => {
      const fixedEndpointId = dexpFixEndpointId(endpoint);

      const { endpointTierLabel, endpointCostKey } =
        getEndpointTierLabelAndCostKey(endpoint, dexpTiers);
      const isSelected =
        endpointsInShoppingCart &&
        !!endpointsInShoppingCart[endpoint.id]?.isSelected;
      return (
        <Fragment key={`endpoint-${i}`}>
          <TableRow
            key={`endpoint-row-${endpoint.id}`}
            highlightOnHover={endpoint.access}
            className={styles.TableRow}
          >
            {selectable && (
              <TableCell width={40} noBorder>
                <Checkbox
                  className={styles.EndpointCheckbox}
                  dataHcName={`${dataHcName}-checkbox-${fixedEndpointId}`}
                  disabled={!endpoint.access}
                  checked={isSelected}
                  onChange={(checked) => {
                    if (checked) {
                      addToCart(endpoint.id, endpoint.name, endpoint.level);
                    } else {
                      removeFromCart(endpoint.id);
                    }
                  }}
                  theme={{
                    checked: styles.checked,
                    Check: styles.Check,
                  }}
                />
              </TableCell>
            )}
            <TableCell width={270} noBorder>
              <b>{endpoint.name}</b>
            </TableCell>
            {orgSsInfo && dexpTiers && (
              <TableCell width={100} noBorder>
                <Tooltip
                  dataHcName={`${dataHcName}-tier-tooltip`}
                  trigger={<span>{endpointTierLabel}</span>}
                  label={
                    <>
                      {endpointTierLabel}
                      <br />
                      {endpointCostKey === null
                        ? '$0'
                        : formatMoney(
                            endpointCostKey &&
                              tierConfig?.costs[endpointCostKey]
                              ? Number(tierConfig.costs[endpointCostKey].value)
                              : null,
                            { precision: 2 }
                          )}
                    </>
                  }
                ></Tooltip>
              </TableCell>
            )}
            <TableCell noBorder>
              <div className={styles.Description}>{endpoint.description}</div>
            </TableCell>
          </TableRow>
        </Fragment>
      );
    });
  };

  const availableEndpointsTableRows = useMemo(() => {
    return getTableRows(availableEndpoints);
  }, [
    endpoints,
    filters,
    endpointsInShoppingCart,
    endpointLevel,
    availableEndpoints,
    dexpTiers,
    orgSsInfo,
  ]);

  const unavailableEndpointsTableRows = useMemo(() => {
    return getTableRows(unavailableEndpoints);
  }, [
    endpoints,
    filters,
    endpointsInShoppingCart,
    endpointLevel,
    unavailableEndpoints,
    dexpTiers,
    orgSsInfo,
  ]);

  if (isInitialLoading) {
    return <LoadingSpinner dataHcName={`${dataHcName}-skeleton`} />;
  }

  return (
    <>
      {availableEndpoints.length > 0 && (
        <Table
          className={classNames(styles.EndpointsDataTable, {
            [styles.shoppingCartCardShowing]:
              endpointsInShoppingCart &&
              Object.keys(endpointsInShoppingCart).length > 0,
          })}
          dataHcName={`available-${dataHcName}-table`}
        >
          {availableEndpointsTableRows}
        </Table>
      )}

      {unavailableEndpoints.length > 0 && (
        <>
          <div className={styles.HeaderContentContainer}>
            <div className={styles.HeaderContent}>Additional data points</div>
            <div className={styles.SubHeaderContent}>
              Contact HouseCanary sales if you are interested in using any of
              these data points.
            </div>
          </div>
          <Table
            className={classNames(styles.EndpointsDataTable, {
              [styles.shoppingCartCardShowing]:
                endpointsInShoppingCart &&
                Object.keys(endpointsInShoppingCart).length > 0,
            })}
            dataHcName={`unavailable-${dataHcName}-table`}
          >
            {unavailableEndpointsTableRows}
          </Table>
        </>
      )}
    </>
  );
};
